/**
 * UserConfigurationsSection
 *
 * Renders and edits mobile app configurations (mobile_app_settings records) stored in the DB.
 * For regular users: shows their own configurations.
 * For admins (roleId <= 2): shows a user-selector and manages configurations for the selected user.
 *
 * Rules enforced here (and independently in the backend):
 *  - No ElevenLabs / OpenAI API calls — audio is played from stored multiVoiceAudioData blobs.
 *  - Voice selection stores the MongoDB `id` (ObjectId); audio is looked up via ElevenLabs `voiceId`.
 *  - source is always 100 for user-generated customizations.
 */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  InputLabel,
  FormControl,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Favorite as FavoriteIcon,
  MusicNote as AudioIcon,
  Person as PersonIcon,
  PlayArrow as PlayIcon,
  RecordVoiceOver as VoiceIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Stop as StopIcon,
  Tune as TuneIcon,
  ViewList as ListIcon,
  GridView as ItemsIcon,
} from '@mui/icons-material';

import {
  GET_USER_CUSTOMIZATIONS_BY_TYPE,
  GET_USER_CUSTOMIZATIONS_FOR_USER,
  GET_ITEMS_BY_CATEGORY,
  GET_CATEGORIES,
  GET_ITEMS_BASIC,
} from '../graphql/queries';
import { GET_VOICES, GET_FILTERED_USERS } from '../graphql/queries';
import {
  UPDATE_USER_CUSTOMIZATION,
  DELETE_USER_CUSTOMIZATION,
  UPDATE_USER_CUSTOMIZATION_FOR_USER,
  DELETE_USER_CUSTOMIZATION_FOR_USER,
  CREATE_USER_CUSTOMIZATION,
  CREATE_USER_CUSTOMIZATION_FOR_USER,
  GENERATE_AUDIO,
  GENERATE_IMAGE,
} from '../graphql/mutations';
import {
  MobileAppCustomizations,
  UserCustomization,
  Voice,
  User,
  Item,
  Category,
  CategoryOrderEntry,
  ItemOrderEntry,
  CustomItemEntry,
  FavoriteItem,
} from '../types';
import { computePermissions } from '../hooks/useUserRole';

// ─── helpers ─────────────────────────────────────────────────────────────────

const CUSTOMIZATION_TYPE = 'mobile_app_settings';

/** Resolve stored MongoDB voice id → ElevenLabs voiceId for audio lookup */
function resolveElevenLabsVoiceId(
  storedId: string | undefined,
  allVoices: Voice[]
): string | undefined {
  if (!storedId || !allVoices.length) return undefined;
  const found = allVoices.find((v) => v.id === storedId || v.voiceId === storedId);
  return found?.voiceId;
}

/** Parse customData JSON, returning null on failure */
function parseCustomData(raw: string): MobileAppCustomizations | null {
  try {
    return JSON.parse(raw) as MobileAppCustomizations;
  } catch {
    return null;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── sub-components ──────────────────────────────────────────────────────────

interface TabPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}
const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

// ─── Edit Dialog ─────────────────────────────────────────────────────────────

interface EditDialogProps {
  open: boolean;
  customization: UserCustomization | null; // null = create mode
  allCustomizations: UserCustomization[];   // all records for duplicate-code check
  allVoices: Voice[];
  targetUserId: string;
  isAdmin: boolean;
  canGenerateAudio: boolean; // roleId <= 98 (paid + admins)
  onClose: () => void;
  onSaved: (updated: UserCustomization) => void;
}

const EMPTY_CONFIG: MobileAppCustomizations = {
  recentlyUsedItems: [],
  itemOrder: {},
  itemVisibility: {},
  customItems: [],
  categoryOrder: [],
  userPreferences: {},
  userSettings: undefined,
  favorites: [],
  lastUpdated: new Date().toISOString(),
};

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  customization,
  allCustomizations,
  allVoices,
  targetUserId,
  isAdmin,
  canGenerateAudio,
  onClose,
  onSaved,
}) => {
  const isCreateMode = !customization;
  const parsed = customization ? parseCustomData(customization.customData) : null;

  // Local state for the editable config
  const [label, setLabel] = useState(customization?.label ?? 'New Configuration');
  const [config, setConfig] = useState<MobileAppCustomizations>(
    parsed ?? { ...EMPTY_CONFIG, lastUpdated: new Date().toISOString() }
  );
  const [tab, setTab] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Audio playback state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);       // custom items
  const [playingItemCode, setPlayingItemCode] = useState<string | null>(null); // standard items
  const [selectedItemCategory, setSelectedItemCategory] = useState<string>('');

  const [updateSelf, { loading: savingSelf }] = useMutation(UPDATE_USER_CUSTOMIZATION);
  const [updateForUser, { loading: savingForUser }] = useMutation(
    UPDATE_USER_CUSTOMIZATION_FOR_USER
  );
  const [createSelf, { loading: creatingSelf }] = useMutation(CREATE_USER_CUSTOMIZATION);
  const [createForUser, { loading: creatingForUser }] = useMutation(
    CREATE_USER_CUSTOMIZATION_FOR_USER
  );
  const [generateAudioMutation] = useMutation(GENERATE_AUDIO);
  const [generateImageMutation] = useMutation(GENERATE_IMAGE);
  const saving = savingSelf || savingForUser || creatingSelf || creatingForUser;

  // ── Category + item data (both create and edit) ───────────────────────────
  // In create mode: pre-populates the config with defaults.
  // In both modes: powers the "Add Custom Item" and "Add Favorite" pickers.
  const { data: defaultCatsData, loading: loadingDefaultCats } = useQuery(GET_CATEGORIES, {
    skip: !open,
    fetchPolicy: 'cache-first',
  });
  const { data: defaultItemsData, loading: loadingDefaultItems } = useQuery(GET_ITEMS_BASIC, {
    skip: !open,
    fetchPolicy: 'cache-first',
  });
  const loadingDefaults = isCreateMode && (loadingDefaultCats || loadingDefaultItems);

  // Reset editable state when the dialog opens in create mode
  useEffect(() => {
    if (open && isCreateMode) {
      setLabel('New Configuration');
      setConfig({ ...EMPTY_CONFIG, lastUpdated: new Date().toISOString() });
      setTab(0);
      setSelectedItemCategory('');
      setSaveError(null);
    }
  }, [open, isCreateMode]);

  // Once the API data has loaded, populate config with the full default set
  // (active categories + their items in default position order).
  useEffect(() => {
    if (!isCreateMode || !open || !defaultCatsData || !defaultItemsData) return;

    const cats: Category[] = [...(defaultCatsData.categories as Category[])]
      .filter((c) => c.status === 1)
      .sort((a, b) => a.createdDate.localeCompare(b.createdDate));

    const allItems = defaultItemsData.items as BasicItem[];

    const categoryOrder: CategoryOrderEntry[] = cats.map((cat, idx) => ({
      categoryCode: cat.code,
      name: cat.name,
      position: idx + 1,
      isVisible: true,
    }));

    const itemOrder: Record<string, ItemOrderEntry[]> = {};
    for (const cat of cats) {
      const catItems = allItems
        .filter((item) => item.categoryCode === cat.code && item.status === 1)
        .sort((a, b) => a.position - b.position);
      if (catItems.length > 0) {
        itemOrder[cat.code] = catItems.map((item, idx) => ({
          itemCode: item.code,
          name: item.name,
          position: idx + 1,
        }));
      }
    }

    setConfig((prev) => ({
      ...prev,
      categoryOrder,
      itemOrder,
      itemVisibility: {},
      lastUpdated: new Date().toISOString(),
    }));
  }, [isCreateMode, open, defaultCatsData, defaultItemsData]);

  // Normalize custom item ids — legacy items stored without an id would cause the edit flow to
  // fall through to the CREATE branch, silently duplicating instead of updating the item.
  useEffect(() => {
    if (isCreateMode) return;
    setConfig((prev) => {
      if ((prev.customItems ?? []).every((it) => !!it.id)) return prev;
      return {
        ...prev,
        customItems: (prev.customItems ?? []).map((item, idx) => ({
          ...item,
          id: item.id || `custom_${item.code || idx}_legacy`,
        })),
      };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch standard items for the selected category (used in Items tab for image/audio)
  // We derive activeCatCode early here so the query can run before render-time computed values
  const earlyActiveCatCode = selectedItemCategory; // refined later; empty string skips query
  const { data: categoryItemsData, loading: categoryItemsLoading } = useQuery(
    GET_ITEMS_BY_CATEGORY,
    {
      variables: { categoryCode: earlyActiveCatCode },
      skip: !open || !earlyActiveCatCode,
      fetchPolicy: 'cache-first',
    }
  );

  // Build a map itemCode → Item for fast lookup in the Items tab
  const apiItemsMap = useMemo<Record<string, Item>>(() => {
    const map: Record<string, Item> = {};
    for (const item of (categoryItemsData?.itemsByCategory ?? []) as Item[]) {
      map[item.code] = item;
    }
    return map;
  }, [categoryItemsData]);

  const handleSave = async () => {
    setSaveError(null);
    const updatedConfig: MobileAppCustomizations = {
      ...config,
      lastUpdated: new Date().toISOString(),
    };
    const input = {
      customizationType: CUSTOMIZATION_TYPE,
      label: label.trim() || (customization?.label ?? 'New Configuration'),
      customData: JSON.stringify(updatedConfig),
      source: 100,
      isActive: true,
      version: isCreateMode ? 1 : (customization!.version ?? 1) + 1,
    };
    try {
      let result: UserCustomization | undefined;
      if (isCreateMode) {
        if (isAdmin) {
          const { data } = await createForUser({ variables: { targetUserId, input } });
          result = data?.createUserCustomizationForUser;
        } else {
          const { data } = await createSelf({ variables: { input } });
          result = data?.createUserCustomization;
        }
      } else if (isAdmin) {
        const { data } = await updateForUser({
          variables: { targetUserId, id: customization!.id, input },
        });
        result = data?.updateUserCustomizationForUser;
      } else {
        const { data } = await updateSelf({
          variables: { id: customization!.id, input },
        });
        result = data?.updateUserCustomization;
      }
      if (result) {
        onSaved(result);
        onClose();
      } else {
        setSaveError('Save returned no result — please try again.');
      }
    } catch (err: any) {
      setSaveError(err.message ?? 'Failed to save configuration.');
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
    setPlayingItemCode(null);
  };

  const playCustomItemAudio = (item: CustomItemEntry) => {
    if (playingId === item.id) {
      stopAudio();
      return;
    }
    stopAudio();

    // Resolve ElevenLabs voiceId from the stored default voice (MongoDB id)
    const elevenLabsVoiceId = resolveElevenLabsVoiceId(
      config.userPreferences?.defaultVoice,
      allVoices
    );

    // multiVoiceAudioData is stored as { voiceId, audioData }[]
    let audioData: string | undefined;
    if (item.multiVoiceAudioData && item.multiVoiceAudioData.length > 0) {
      const match = elevenLabsVoiceId
        ? item.multiVoiceAudioData.find((m) => m.voiceId === elevenLabsVoiceId)
        : undefined;
      audioData = match?.audioData ?? item.multiVoiceAudioData[0]?.audioData;
    }

    if (!audioData) {
      // Final fallback: browser TTS (no API call)
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(item.name);
        window.speechSynthesis.speak(utterance);
        setPlayingId(item.id);
        utterance.onend = () => setPlayingId(null);
      }
      return;
    }

    try {
      const audioSrc = audioData.startsWith('data:') ? audioData : `data:audio/mpeg;base64,${audioData}`;
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      setPlayingId(item.id);
      audio.play();
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
    } catch {
      setPlayingId(null);
    }
  };

  /** Play audio for a standard PECS item using its soundOptions from the API */
  const playStandardItemAudio = (itemCode: string, itemName: string) => {
    if (playingItemCode === itemCode) {
      stopAudio();
      setPlayingItemCode(null);
      return;
    }
    stopAudio();
    setPlayingItemCode(null);

    const apiItem = apiItemsMap[itemCode];
    if (!apiItem?.soundOptions?.length) {
      // Fallback: browser TTS
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(itemName);
        window.speechSynthesis.speak(utterance);
        setPlayingItemCode(itemCode);
        utterance.onend = () => setPlayingItemCode(null);
      }
      return;
    }

    // Resolve preferred voice
    const elevenLabsVoiceId = resolveElevenLabsVoiceId(
      config.userPreferences?.defaultVoice,
      allVoices
    );
    const option = elevenLabsVoiceId
      ? (apiItem.soundOptions.find((s) => s.voiceId === elevenLabsVoiceId) ?? apiItem.soundOptions[0])
      : apiItem.soundOptions[0];

    const rawData = option.soundData;
    if (!rawData) return;

    try {
      const audioSrc = rawData.startsWith('data:') ? rawData : `data:audio/mpeg;base64,${rawData}`;
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      setPlayingItemCode(itemCode);
      audio.play();
      audio.onended = () => setPlayingItemCode(null);
      audio.onerror = () => setPlayingItemCode(null);
    } catch {
      setPlayingItemCode(null);
    }
  };

  // Setters for nested config fields
  const setUserSettings = (partial: Partial<NonNullable<typeof config.userSettings>>) => {
    setConfig((prev) => ({
      ...prev,
      userSettings: {
        isAiEnabled: false,
        enableHapticFeedback: true,
        enableSoundEffects: true,
        enableDarkMode: false,
        autoSyncEnabled: true,
        showRecentItemCount: true,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: 'web',
        ...prev.userSettings,
        ...partial,
      },
    }));
  };

  const setDefaultVoice = (mongoId: string) => {
    setConfig((prev) => ({
      ...prev,
      userPreferences: { ...prev.userPreferences, defaultVoice: mongoId },
    }));
  };

  const setCategoryVisible = (categoryCode: string, isVisible: boolean) => {
    setConfig((prev) => ({
      ...prev,
      categoryOrder: prev.categoryOrder.map((c) =>
        c.categoryCode === categoryCode ? { ...c, isVisible } : c
      ),
    }));
  };

  const moveCategoryUp = (index: number) => {
    if (index === 0) return;
    setConfig((prev) => {
      const arr = [...prev.categoryOrder];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return { ...prev, categoryOrder: arr.map((c, i) => ({ ...c, position: i })) };
    });
  };

  const moveCategoryDown = (index: number) => {
    setConfig((prev) => {
      if (index >= prev.categoryOrder.length - 1) return prev;
      const arr = [...prev.categoryOrder];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return { ...prev, categoryOrder: arr.map((c, i) => ({ ...c, position: i })) };
    });
  };

  const deleteCustomItem = (itemId: string) => {
    setConfig((prev) => ({
      ...prev,
      customItems: prev.customItems.filter((ci) => ci.id !== itemId),
    }));
  };

  const removeFavorite = (itemCode: string) => {
    setConfig((prev) => ({
      ...prev,
      favorites: (prev.favorites ?? []).filter((f) => f.itemCode !== itemCode),
    }));
  };

  // ── Add custom item ───────────────────────────────────────────────────────
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [newCustomName, setNewCustomName] = useState('');
  const [newCustomCode, setNewCustomCode] = useState('');
  const [newCustomCodeEdited, setNewCustomCodeEdited] = useState(false);
  const [newCustomCat, setNewCustomCat] = useState('');
  const [newCustomDesc, setNewCustomDesc] = useState('');
  const [newCustomAudioText, setNewCustomAudioText] = useState('');
  const [newCustomAudioTextEdited, setNewCustomAudioTextEdited] = useState(false);
  const [newCustomImageData, setNewCustomImageData] = useState('');
  const [newCustomMultiVoiceAudio, setNewCustomMultiVoiceAudio] = useState<{ voiceId: string; audioData: string }[]>([]);
  const [genAudioLoading, setGenAudioLoading] = useState(false);
  const [genImageLoading, setGenImageLoading] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [editingCustomItemId, setEditingCustomItemId] = useState<string | null>(null);

  const toPascalCase = (s: string) =>
    s.split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('').replace(/[^a-zA-Z0-9]/g, '');

  const resetCustomItemForm = () => {
    setNewCustomName('');
    setNewCustomCode('');
    setNewCustomCodeEdited(false);
    setNewCustomCat('');
    setNewCustomDesc('');
    setNewCustomAudioText('');
    setNewCustomAudioTextEdited(false);
    setNewCustomImageData('');
    setNewCustomMultiVoiceAudio([]);
    setGenError(null);
    setEditingCustomItemId(null);
  };

  const startEditCustomItem = (item: CustomItemEntry) => {
    setNewCustomName(item.name);
    setNewCustomCode(item.code);
    setNewCustomCodeEdited(true);
    setNewCustomCat(item.categoryCode);
    setNewCustomDesc(item.description ?? '');
    setNewCustomAudioText(item.audioText ?? '');
    setNewCustomAudioTextEdited(item.audioText ? true : false);
    setNewCustomImageData(item.imageData ?? '');
    setNewCustomMultiVoiceAudio(item.multiVoiceAudioData ?? []);
    setGenError(null);
    setEditingCustomItemId(item.id);
    setShowAddCustom(true);
  };

  const handleCustomItemImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setNewCustomImageData(reader.result as string);
    reader.readAsDataURL(file);
  };

  const generateCustomItemImage = async () => {
    const name = newCustomName.trim();
    if (!name) return;
    setGenImageLoading(true);
    setGenError(null);
    try {
      const prompt = `PECS AAC communication card for "${name}": simple, clear, cartoon-style illustration on white background`;
      const { data } = await generateImageMutation({
        variables: { input: { prompt, size: '1024x1024', quality: 'standard', returnBase64: true } },
      });
      if (data?.generateImage?.imageBase64) {
        setNewCustomImageData(`data:image/png;base64,${data.generateImage.imageBase64}`);
      }
    } catch (e: any) {
      setGenError(e.message ?? 'Failed to generate image');
    } finally {
      setGenImageLoading(false);
    }
  };

  const generateCustomItemAudio = async () => {
    const text = newCustomAudioText.trim();
    if (!text || allVoices.length === 0) return;
    setGenAudioLoading(true);
    setGenError(null);
    try {
      const results: { voiceId: string; audioData: string }[] = [];
      for (const voice of allVoices) {
        if (!voice.voiceId) continue;
        const { data } = await generateAudioMutation({
          variables: { input: { text, voiceId: voice.voiceId } },
        });
        if (data?.generateAudio?.audioBase64) {
          results.push({ voiceId: voice.voiceId, audioData: data.generateAudio.audioBase64 });
        }
      }
      setNewCustomMultiVoiceAudio(results);
    } catch (e: any) {
      setGenError(e.message ?? 'Failed to generate audio');
    } finally {
      setGenAudioLoading(false);
    }
  };

  const addCustomItem = () => {
    const name = newCustomName.trim();
    const code = newCustomCode.trim();
    if (!name || !code || !newCustomCat) return;
    if (editingCustomItemId) {
      // Update existing item
      setConfig((prev) => ({
        ...prev,
        customItems: (prev.customItems ?? []).map((it) =>
          it.id === editingCustomItemId
            ? {
                ...it,
                name,
                code,
                description: newCustomDesc.trim() || undefined,
                categoryCode: newCustomCat,
                audioText: newCustomAudioText.trim() || undefined,
                imageData: newCustomImageData || undefined,
                multiVoiceAudioData: newCustomMultiVoiceAudio.length > 0 ? newCustomMultiVoiceAudio : undefined,
              }
            : it
        ),
      }));
    } else {
      const item: CustomItemEntry = {
        id: `custom_${Date.now()}`,
        code,
        name,
        description: newCustomDesc.trim() || undefined,
        categoryCode: newCustomCat,
        audioText: newCustomAudioText.trim() || undefined,
        imageData: newCustomImageData || undefined,
        multiVoiceAudioData: newCustomMultiVoiceAudio.length > 0 ? newCustomMultiVoiceAudio : undefined,
        createdAt: new Date().toISOString(),
        isCustom: true,
      };
      setConfig((prev) => ({ ...prev, customItems: [...(prev.customItems ?? []), item] }));
    }
    resetCustomItemForm();
    setShowAddCustom(false);
  };

  // ── Add favorite ─────────────────────────────────────────────────────────
  const [showAddFav, setShowAddFav] = useState(false);
  const [newFavCat, setNewFavCat] = useState('');
  const [newFavItem, setNewFavItem] = useState('');

  // All active categories available for pickers
  const allCatsForPicker: Category[] = ((defaultCatsData?.categories ?? []) as Category[])
    .filter((c) => c.status === 1)
    .sort((a, b) => a.createdDate.localeCompare(b.createdDate));

  interface BasicItem { code: string; name: string; position: number; categoryCode: string; status: number; }
  const allItemsForPicker: BasicItem[] = (defaultItemsData?.items ?? []) as BasicItem[];

  const itemsForFavPicker = allItemsForPicker
    .filter((i) => i.categoryCode === newFavCat && i.status === 1)
    .sort((a, b) => a.name.localeCompare(b.name));

  const addFavorite = () => {
    if (!newFavItem || !newFavCat) return;
    if ((config.favorites ?? []).some((f) => f.itemCode === newFavItem && f.categoryCode === newFavCat)) return;
    const fav: FavoriteItem = {
      itemCode: newFavItem,
      categoryCode: newFavCat,
      addedDate: new Date().toISOString(),
      isAutoGenerated: false,
      position: (config.favorites ?? []).length + 1,
      source: 'user',
    };
    setConfig((prev) => ({ ...prev, favorites: [...(prev.favorites ?? []), fav] }));
    setNewFavCat('');
    setNewFavItem('');
    setShowAddFav(false);
  };

  const setItemVisible = (categoryCode: string, itemCode: string, isVisible: boolean) => {
    setConfig((prev) => {
      const existing = prev.itemVisibility[categoryCode] ?? [];
      const idx = existing.findIndex((e) => e.itemCode === itemCode);
      const updated =
        idx >= 0
          ? existing.map((e) => (e.itemCode === itemCode ? { ...e, isVisible } : e))
          : [...existing, { itemCode, isVisible }];
      return { ...prev, itemVisibility: { ...prev.itemVisibility, [categoryCode]: updated } };
    });
  };

  const moveItemUp = (categoryCode: string, index: number) => {
    if (index === 0) return;
    setConfig((prev) => {
      const arr = [...(prev.itemOrder[categoryCode] ?? [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return {
        ...prev,
        itemOrder: { ...prev.itemOrder, [categoryCode]: arr.map((e, i) => ({ ...e, position: i })) },
      };
    });
  };

  const moveItemDown = (categoryCode: string, index: number) => {
    setConfig((prev) => {
      const arr = [...(prev.itemOrder[categoryCode] ?? [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return {
        ...prev,
        itemOrder: { ...prev.itemOrder, [categoryCode]: arr.map((e, i) => ({ ...e, position: i })) },
      };
    });
  };

  const settings = config.userSettings;
  const categories: CategoryOrderEntry[] = [...(config.categoryOrder ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  );
  const customItems: CustomItemEntry[] = config.customItems ?? [];

  // All custom item codes across every config record (for cross-record duplicate check)
  const allCustomItemCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const rec of allCustomizations) {
      const parsed2 = parseCustomData(rec.customData);
      for (const ci of parsed2?.customItems ?? []) {
        codes.add(ci.code);
      }
    }
    return codes;
  }, [allCustomizations]);

  const favorites: FavoriteItem[] = config.favorites ?? [];
  const defaultVoiceId = config.userPreferences?.defaultVoice;

  // Items per category — merge itemOrder (position) + itemVisibility (isVisible)
  const categoriesWithItems = categories.filter(
    (cat) =>
      (config.itemOrder[cat.categoryCode]?.length ?? 0) > 0 ||
      (config.itemVisibility[cat.categoryCode]?.length ?? 0) > 0
  );
  const activeCatCode = selectedItemCategory || (categoriesWithItems[0]?.categoryCode ?? '');
  const itemsForCategory = (() => {
    if (!activeCatCode) return [];
    const order = [...(config.itemOrder[activeCatCode] ?? [])].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );
    const visMap: Record<string, boolean> = {};
    for (const v of config.itemVisibility[activeCatCode] ?? []) {
      visMap[v.itemCode] = v.isVisible;
    }
    return order.map((entry) => ({
      code: entry.itemCode,
      name: entry.name,
      position: entry.position ?? 0,
      isVisible: visMap[entry.itemCode] ?? true,
    }));
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" fontWeight={700} component="div">
          {isCreateMode ? 'New Configuration' : 'Edit Configuration'}
        </Typography>
        <TextField
          label="Configuration name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          size="small"
          fullWidth
          sx={{ mt: 1.5 }}
        />
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loadingDefaults ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading default categories and items…
            </Typography>
          </Box>
        ) : (
          <>
          <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<SettingsIcon fontSize="small" />} label="Settings" iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
          <Tab icon={<VoiceIcon fontSize="small" />} label="Voice" iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
          <Tab icon={<ListIcon fontSize="small" />} label="Categories" iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
          <Tab icon={<ItemsIcon fontSize="small" />} label={`Items (${Object.values(config.itemOrder).reduce((n, a) => n + a.length, 0)})`} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
          <Tab icon={<AudioIcon fontSize="small" />} label={`Custom Items (${customItems.length})`} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
          <Tab icon={<FavoriteIcon fontSize="small" />} label={`Favorites (${favorites.length})`} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
        </Tabs>

        <Box sx={{ px: 3, py: 1 }}>
          {/* ── Tab 0: General Settings ── */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  App Behaviour
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.isAiEnabled ?? false}
                      onChange={(e) => setUserSettings({ isAiEnabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>AI Suggestions</Typography>
                      <Typography variant="caption" color="text.secondary">Enable AI-powered item ordering</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.enableHapticFeedback ?? true}
                      onChange={(e) => setUserSettings({ enableHapticFeedback: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Haptic Feedback</Typography>
                      <Typography variant="caption" color="text.secondary">Vibrate on item tap</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.enableSoundEffects ?? true}
                      onChange={(e) => setUserSettings({ enableSoundEffects: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Sound Effects</Typography>
                      <Typography variant="caption" color="text.secondary">UI sounds on interactions</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.enableDarkMode ?? false}
                      onChange={(e) => setUserSettings({ enableDarkMode: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Dark Mode</Typography>
                      <Typography variant="caption" color="text.secondary">Use dark theme in app</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.autoSyncEnabled ?? true}
                      onChange={(e) => setUserSettings({ autoSyncEnabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Auto Sync</Typography>
                      <Typography variant="caption" color="text.secondary">Automatically sync settings</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.showRecentItemCount ?? true}
                      onChange={(e) => setUserSettings({ showRecentItemCount: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Show Recent Count</Typography>
                      <Typography variant="caption" color="text.secondary">Display recent item count badges</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Voice Speed
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settings?.voiceSpeed != null ? `${settings.voiceSpeed}%` : 'Default (100%)'}
                </Typography>
                <Slider
                  value={settings?.voiceSpeed ?? 100}
                  min={50}
                  max={200}
                  step={5}
                  marks={[
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                    { value: 200, label: '200%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  onChange={(_, v) => setUserSettings({ voiceSpeed: v as number })}
                  sx={{ mt: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Voice Volume
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settings?.voiceVolume != null
                    ? `${Math.round((settings.voiceVolume ?? 1) * 100)}%`
                    : 'Default (100%)'}
                </Typography>
                <Slider
                  value={settings?.voiceVolume != null ? Math.round(settings.voiceVolume * 100) : 100}
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  onChange={(_, v) => setUserSettings({ voiceVolume: (v as number) / 100 })}
                  sx={{ mt: 2 }}
                />
              </Grid>

              {settings?.maxRecentItemsDisplay != null && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Max recent items to display"
                    type="number"
                    value={settings.maxRecentItemsDisplay}
                    onChange={(e) =>
                      setUserSettings({ maxRecentItemsDisplay: parseInt(e.target.value, 10) || 10 })
                    }
                    size="small"
                    inputProps={{ min: 1, max: 50 }}
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* ── Tab 1: Voice ── */}
          <TabPanel value={tab} index={1}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Default Voice
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Select which voice is used when speaking items. The voice is stored by its database ID.
            </Typography>
            {allVoices.length === 0 ? (
              <Alert severity="info">Loading voices…</Alert>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel shrink>Default voice</InputLabel>
                <Select
                  label="Default voice"
                  value={defaultVoiceId ?? ''}
                  onChange={(e) => setDefaultVoice(e.target.value)}
                  notched
                  displayEmpty
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">
                    <em>No default voice selected</em>
                  </MenuItem>
                  {allVoices.filter((v) => v.isActive).map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {v.name}
                          {v.isDefault && (
                            <Chip label="default" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Typography>
                        {v.description && (
                          <Typography variant="caption" color="text.secondary">
                            {v.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {defaultVoiceId && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Selected: <strong>{allVoices.find((v) => v.id === defaultVoiceId)?.name ?? 'Unknown'}</strong>
                  {' '}(ElevenLabs ID: {resolveElevenLabsVoiceId(defaultVoiceId, allVoices) ?? '—'})
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* ── Tab 2: Categories ── */}
          <TabPanel value={tab} index={2}>
            {categories.length === 0 ? (
              <Alert severity="info">No category ordering data in this configuration.</Alert>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Category Order &amp; Visibility
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Drag categories or use the arrows to reorder. Toggle visibility to show/hide in the app.
                </Typography>
                <List dense disablePadding>
                  {categories.map((cat, idx) => (
                    <ListItem
                      key={cat.categoryCode ?? `cat-${idx}`}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        mb: 0.75,
                        bgcolor: cat.isVisible ? 'background.paper' : 'action.hover',
                        opacity: cat.isVisible ? 1 : 0.6,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                        <IconButton size="small" onClick={() => moveCategoryUp(idx)} disabled={idx === 0}>
                          <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveCategoryDown(idx)}
                          disabled={idx === categories.length - 1}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {cat.name || cat.categoryCode}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Position: {idx + 1} · Code: {cat.categoryCode}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={cat.isVisible}
                              onChange={(e) => setCategoryVisible(cat.categoryCode, e.target.checked)}
                              size="small"
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="caption">
                              {cat.isVisible ? 'Visible' : 'Hidden'}
                            </Typography>
                          }
                          labelPlacement="start"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </TabPanel>

          {/* ── Tab 3: Items (itemOrder + itemVisibility) ── */}
          <TabPanel value={tab} index={3}>
            {categoriesWithItems.length === 0 ? (
              <Alert severity="info">No item ordering data in this configuration.</Alert>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Item Order &amp; Visibility
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Select a category to reorder its items or toggle their visibility in the app.
                </Typography>
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                  <InputLabel shrink>Category</InputLabel>
                  <Select
                    label="Category"
                    value={activeCatCode}
                    onChange={(e) => setSelectedItemCategory(e.target.value)}
                    notched
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {categoriesWithItems.map((cat) => (
                      <MenuItem key={cat.categoryCode} value={cat.categoryCode}>
                        {cat.name || cat.categoryCode} ({config.itemOrder[cat.categoryCode]?.length ?? 0} items)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {itemsForCategory.length === 0 ? (
                  <Alert severity="info">No items for this category.</Alert>
                ) : categoryItemsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {itemsForCategory.map((item, idx) => {
                      const apiItem = apiItemsMap[item.code];
                      const hasAudio = (apiItem?.soundOptions?.length ?? 0) > 0;
                      const imgRaw = apiItem?.imageData;
                      const imgSrc = imgRaw
                        ? imgRaw.startsWith('data:')
                          ? imgRaw
                          : `data:image/png;base64,${imgRaw}`
                        : undefined;
                      return (
                        <ListItem
                          key={item.code ?? `item-${idx}`}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            mb: 0.75,
                            bgcolor: item.isVisible ? 'background.paper' : 'action.hover',
                            opacity: item.isVisible ? 1 : 0.6,
                            pr: '180px', // room for right-side actions
                          }}
                        >
                          {/* reorder buttons */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => moveItemUp(activeCatCode, idx)}
                              disabled={idx === 0}
                            >
                              <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveItemDown(activeCatCode, idx)}
                              disabled={idx === itemsForCategory.length - 1}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          {/* thumbnail */}
                          {imgSrc ? (
                            <Box
                              component="img"
                              src={imgSrc}
                              alt={item.name || item.code}
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'contain',
                                borderRadius: 1,
                                mr: 1.5,
                                flexShrink: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.default',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                mr: 1.5,
                                flexShrink: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'action.hover',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <ItemsIcon fontSize="small" color="disabled" />
                            </Box>
                          )}

                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {item.name || item.code}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Position: {idx + 1} · Code: {item.code}
                                {hasAudio && ` · ${apiItem!.soundOptions!.length} voice(s)`}
                              </Typography>
                            }
                          />

                          <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {/* play button */}
                            <Tooltip title={hasAudio ? (playingItemCode === item.code ? 'Stop' : 'Play audio') : 'No audio stored'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color={playingItemCode === item.code ? 'error' : 'primary'}
                                  onClick={() => playStandardItemAudio(item.code, item.name || item.code)}
                                  disabled={!hasAudio}
                                >
                                  {playingItemCode === item.code
                                    ? <StopIcon fontSize="small" />
                                    : <PlayIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>

                            {/* visibility toggle */}
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={item.isVisible}
                                  onChange={(e) =>
                                    setItemVisible(activeCatCode, item.code, e.target.checked)
                                  }
                                  size="small"
                                  color="primary"
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  {item.isVisible ? 'Visible' : 'Hidden'}
                                </Typography>
                              }
                              labelPlacement="start"
                              sx={{ ml: 0 }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </>
            )}
          </TabPanel>

          {/* ── Tab 4: Custom Items ── */}
          <TabPanel value={tab} index={4}>
            {/* Add custom item form */}
            {showAddCustom ? (
              <Box sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 2, p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>{editingCustomItemId ? 'Edit Custom Item' : 'Add Custom Item'}</Typography>
                {genError && (
                  <Alert severity="error" sx={{ mb: 1.5, py: 0 }} onClose={() => setGenError(null)}>{genError}</Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Name *"
                    size="small"
                    fullWidth
                    value={newCustomName}
                    onChange={(e) => {
                      setNewCustomName(e.target.value);
                      if (!newCustomCodeEdited) setNewCustomCode(toPascalCase(e.target.value));
                      if (!newCustomAudioTextEdited) setNewCustomAudioText(e.target.value);
                    }}
                  />
                  <TextField
                    label="Code *"
                    size="small"
                    fullWidth
                    value={newCustomCode}
                    onChange={(e) => { setNewCustomCode(e.target.value); setNewCustomCodeEdited(true); }}
                    error={
                      !!newCustomCode.trim() &&
                      (() => {
                        const trimmed = newCustomCode.trim();
                        // Check current config's items excluding the one being edited
                        if ((customItems ?? []).some((it) => it.code === trimmed && it.id !== editingCustomItemId)) return true;
                        // Check all other config records
                        for (const rec of allCustomizations) {
                          if (rec.id === customization?.id) continue;
                          const p = parseCustomData(rec.customData);
                          if ((p?.customItems ?? []).some((ci) => ci.code === trimmed)) return true;
                        }
                        return false;
                      })()
                    }
                    helperText={
                      !!newCustomCode.trim() &&
                      (() => {
                        const trimmed = newCustomCode.trim();
                        if ((customItems ?? []).some((it) => it.code === trimmed && it.id !== editingCustomItemId)) return true;
                        for (const rec of allCustomizations) {
                          if (rec.id === customization?.id) continue;
                          const p = parseCustomData(rec.customData);
                          if ((p?.customItems ?? []).some((ci) => ci.code === trimmed)) return true;
                        }
                        return false;
                      })()
                        ? 'A custom item with this code already exists'
                        : 'Unique identifier (auto-filled from name)'
                    }
                  />
                  <TextField
                    label="Category *"
                    size="small"
                    select
                    fullWidth
                    value={newCustomCat}
                    onChange={(e) => setNewCustomCat(e.target.value)}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
                  >
                    {allCatsForPicker.map((c) => (
                      <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    value={newCustomDesc}
                    onChange={(e) => setNewCustomDesc(e.target.value)}
                  />

                  {/* ── Audio Text ── */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        label="Audio Text"
                        size="small"
                        fullWidth
                        value={newCustomAudioText}
                        onChange={(e) => { setNewCustomAudioText(e.target.value); setNewCustomAudioTextEdited(true); }}
                        helperText="Text used for speech synthesis on the mobile app"
                      />
                      {canGenerateAudio && (
                        <Tooltip title={
                          !newCustomAudioText.trim() ? 'Enter audio text first' :
                          genAudioLoading ? 'Generating…' :
                          newCustomMultiVoiceAudio.length > 0 ? `Regenerate (${newCustomMultiVoiceAudio.length} voice${newCustomMultiVoiceAudio.length !== 1 ? 's' : ''} stored)` :
                          `Generate audio for all ${allVoices.length} voice${allVoices.length !== 1 ? 's' : ''}`
                        }>
                          <span>
                            <Button
                              size="small"
                              variant={newCustomMultiVoiceAudio.length > 0 ? 'contained' : 'outlined'}
                              color={newCustomMultiVoiceAudio.length > 0 ? 'success' : 'primary'}
                              onClick={generateCustomItemAudio}
                              disabled={!newCustomAudioText.trim() || genAudioLoading}
                              startIcon={genAudioLoading ? <CircularProgress size={14} color="inherit" /> : <AudioIcon />}
                              sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: '-18px', flexShrink: 0 }}
                            >
                              {newCustomMultiVoiceAudio.length > 0 ? `${newCustomMultiVoiceAudio.length} voice${newCustomMultiVoiceAudio.length !== 1 ? 's' : ''}` : 'Gen Audio'}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {/* ── Image ── */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Image</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {newCustomImageData ? (
                        <Box sx={{ position: 'relative', flexShrink: 0 }}>
                          <Box
                            component="img"
                            src={newCustomImageData}
                            alt="preview"
                            sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setNewCustomImageData('')}
                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', width: 20, height: 20 }}
                          >
                            <DeleteIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ width: 64, height: 64, borderRadius: 1, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <AudioIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {canGenerateAudio && (
                          <Tooltip title={!newCustomName.trim() ? 'Enter a name first' : genImageLoading ? 'Generating…' : 'Generate image with AI'}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={generateCustomItemImage}
                                disabled={!newCustomName.trim() || genImageLoading}
                                startIcon={genImageLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                                sx={{ textTransform: 'none' }}
                              >
                                {genImageLoading ? 'Generating…' : 'Generate Image'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => { resetCustomItemForm(); setShowAddCustom(false); }} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={addCustomItem} disabled={!newCustomName.trim() || !newCustomCode.trim() || !newCustomCat || genAudioLoading || genImageLoading || (() => { const trimmed = newCustomCode.trim(); if ((customItems ?? []).some((it) => it.code === trimmed && it.id !== editingCustomItemId)) return true; for (const rec of allCustomizations) { if (rec.id === customization?.id) continue; const p = parseCustomData(rec.customData); if ((p?.customItems ?? []).some((ci) => ci.code === trimmed)) return true; } return false; })()} sx={{ textTransform: 'none' }}>{editingCustomItemId ? 'Save Changes' : 'Add Item'}</Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddCustom(true)} sx={{ textTransform: 'none', mb: 2 }}>
                Add Custom Item
              </Button>
            )}
            {customItems.length === 0 ? (
              <Alert severity="info">No custom items in this configuration.</Alert>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Custom Items
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Audio is played from stored data — no external API calls. Use the delete button to remove a custom item.
                </Typography>
                <List dense disablePadding>
                  {customItems.map((item, idx) => (
                    <ListItem
                      key={item.id ?? item.code ?? `custom-${idx}`}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        mb: 0.75,
                        bgcolor: 'background.paper',
                        alignItems: 'center',
                      }}
                    >
                      {item.imageData ? (
                        <Box
                          component="img"
                          src={item.imageData.startsWith('data:') ? item.imageData : `data:image/png;base64,${item.imageData}`}
                          alt={item.name}
                          sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, mr: 1.5, flexShrink: 0 }}
                        />
                      ) : (
                        <Box sx={{ width: 48, height: 48, borderRadius: 1, mr: 1.5, bgcolor: 'action.hover', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <AudioIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        </Box>
                      )}
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {item.description ? `${item.description} · ` : ''}
                            Category: {item.categoryCode}
                            {item.multiVoiceAudioData &&
                              item.multiVoiceAudioData.length > 0 &&
                              ` · ${item.multiVoiceAudioData.length} voice(s) stored`}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip
                          title={
                            !item.multiVoiceAudioData ||
                            item.multiVoiceAudioData.length === 0
                              ? 'No audio stored'
                              : playingId === item.id
                              ? 'Stop'
                              : 'Play audio'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color={playingId === item.id ? 'error' : 'primary'}
                              onClick={() => playCustomItemAudio(item)}
                              disabled={
                                !item.multiVoiceAudioData ||
                                item.multiVoiceAudioData.length === 0
                              }
                            >
                              {playingId === item.id ? (
                                <StopIcon fontSize="small" />
                              ) : (
                                <PlayIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit item">
                          <IconButton size="small" color="default" onClick={() => startEditCustomItem(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from this configuration">
                          <IconButton size="small" color="error" onClick={() => deleteCustomItem(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </TabPanel>

          {/* ── Tab 5: Favorites ── */}
          <TabPanel value={tab} index={5}>
            {/* Add favorite form */}
            {showAddFav ? (
              <Box sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 2, p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Add Favorite</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Category *"
                    size="small"
                    select
                    fullWidth
                    value={newFavCat}
                    onChange={(e) => { setNewFavCat(e.target.value); setNewFavItem(''); }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
                  >
                    {allCatsForPicker.map((c) => (
                      <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                    ))}
                  </TextField>
                  <Autocomplete
                    options={itemsForFavPicker}
                    getOptionLabel={(o) => o.name}
                    getOptionDisabled={(o) =>
                      (config.favorites ?? []).some(
                        (f) => f.itemCode === o.code && f.categoryCode === newFavCat
                      )
                    }
                    value={itemsForFavPicker.find((i) => i.code === newFavItem) ?? null}
                    onChange={(_, val) => setNewFavItem(val?.code ?? '')}
                    disabled={!newFavCat}
                    size="small"
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} label={newFavCat ? `Item * (${itemsForFavPicker.length})` : 'Item *'} placeholder="Search…" />
                    )}
                    ListboxProps={{ sx: { maxHeight: 260 } }}
                    noOptionsText="No items found"
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => { setShowAddFav(false); setNewFavCat(''); setNewFavItem(''); }} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={addFavorite} disabled={!newFavCat || !newFavItem} sx={{ textTransform: 'none' }}>Add Favorite</Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddFav(true)} sx={{ textTransform: 'none', mb: 2 }}>
                Add Favorite
              </Button>
            )}
            {favorites.length === 0 ? (
              <Alert severity="info">No favorites saved in this configuration.</Alert>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Favorited Items
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  These item codes are marked as favorites in the app. Remove items you no longer want favorited.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {favorites.map((fav) => (
                    <Chip
                      key={`${fav.categoryCode}-${fav.itemCode}`}
                      label={`${fav.itemCode}${fav.prefixItemCode ? ` (${fav.prefixItemCode})` : ''}`}
                      icon={<FavoriteIcon />}
                      onDelete={() => removeFavorite(fav.itemCode)}
                      color={fav.isAutoGenerated ? 'primary' : 'default'}
                      variant="outlined"
                      size="small"
                      title={`Category: ${fav.categoryCode} · Source: ${fav.source}`}
                    />
                  ))}
                </Box>
              </>
            )}
          </TabPanel>
        </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {saveError && (
            <Alert severity="error" sx={{ py: 0 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} variant="text" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 110 }}
          >
            {saving ? (isCreateMode ? 'Creating…' : 'Saving…') : (isCreateMode ? 'Create' : 'Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Section Component ───────────────────────────────────────────────────

interface UserConfigurationsSectionProps {
  user: User;
}

export const UserConfigurationsSection: React.FC<UserConfigurationsSectionProps> = ({ user }) => {
  const { isClientAdmin: isAdmin, isPaidUser } = computePermissions(user);

  // Admin user selection — defaults to the logged-in user
  const [selectedUserId, setSelectedUserId] = useState<string>(user.id);
  const targetUserId = isAdmin && selectedUserId ? selectedUserId : user.id;
  const managingSelf = targetUserId === user.id;

  // Dialog state
  const [editTarget, setEditTarget] = useState<UserCustomization | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState<UserCustomization | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Success snackbar
  const [snack, setSnack] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  // ── Data queries ──
  const { data: voicesData } = useQuery(GET_VOICES);
  const allVoices: Voice[] = voicesData?.voices ?? [];

  const { data: filteredUsersData } = useQuery(GET_FILTERED_USERS, {
    skip: !isAdmin,
  });
  const filteredUsers: User[] = filteredUsersData?.filteredUsers ?? [];

  // Own configs
  const {
    data: selfConfigData,
    loading: loadingSelfConfigs,
    error: selfConfigError,
    refetch: refetchSelf,
  } = useQuery(GET_USER_CUSTOMIZATIONS_BY_TYPE, {
    variables: { customizationType: CUSTOMIZATION_TYPE },
    skip: !managingSelf,
    fetchPolicy: 'cache-and-network',
  });

  // Target user's configs (admin only)
  const {
    data: targetConfigData,
    loading: loadingTargetConfigs,
    error: targetConfigError,
    refetch: refetchTarget,
  } = useQuery(GET_USER_CUSTOMIZATIONS_FOR_USER, {
    variables: { userId: targetUserId },
    skip: managingSelf || !selectedUserId,
    fetchPolicy: 'cache-and-network',
  });

  // Local overrides applied immediately after a save, before the refetch resolves
  const [savedOverrides, setSavedOverrides] = useState<Record<string, UserCustomization>>({});

  const configurations: UserCustomization[] = useMemo(() => {
    const base = managingSelf
      ? (selfConfigData?.userCustomizationsByType ?? [])
      : (targetConfigData?.userCustomizationsForUser ?? []);
    return (base as UserCustomization[]).map((c) => savedOverrides[c.id] ?? c);
  }, [managingSelf, selfConfigData, targetConfigData, savedOverrides]);
  const loading = managingSelf ? loadingSelfConfigs : loadingTargetConfigs;
  const queryError = managingSelf ? selfConfigError : targetConfigError;

  const refetch = useCallback(() => {
    if (managingSelf) refetchSelf();
    else refetchTarget();
  }, [managingSelf, refetchSelf, refetchTarget]);

  // ── Mutations ──
  const [deleteSelf, { loading: deletingSelf }] = useMutation(DELETE_USER_CUSTOMIZATION);
  const [deleteForUser, { loading: deletingForUser }] = useMutation(
    DELETE_USER_CUSTOMIZATION_FOR_USER
  );
  const deleting = deletingSelf || deletingForUser;

  const handleDeleteConfirmed = async () => {
    if (!deleteConfirm) return;
    setDeleteError(null);
    try {
      if (managingSelf) {
        await deleteSelf({ variables: { id: deleteConfirm.id } });
      } else {
        await deleteForUser({ variables: { targetUserId, id: deleteConfirm.id } });
      }
      setDeleteConfirm(null);
      setSnack({ open: true, message: 'Configuration deleted.' });
      refetch();
    } catch (err: any) {
      setDeleteError(err.message ?? 'Failed to delete configuration.');
    }
  };

  const handleSaved = (updated: UserCustomization) => {
    // Apply immediately so the list reflects the change before the background refetch resolves
    setSavedOverrides((prev) => ({ ...prev, [updated.id]: updated }));
    setSnack({ open: true, message: 'Configuration saved.' });
    refetch();
  };

  const selectedUser = filteredUsers.find((u) => u.id === selectedUserId);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, gap: 2 }}>
        {/* Left: title + optional "managing" indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
            <TuneIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="h6" fontWeight={600} noWrap>
              User Configurations
            </Typography>
            {!managingSelf && selectedUser && (
              <Chip
                icon={<PersonIcon />}
                label={`Managing: ${selectedUser.displayName || selectedUser.username}`}
                size="small"
                color="warning"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          {/* Right: action button — fixed width so it never wraps */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            New Configuration
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Admin: user selector */}
        {isAdmin && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f4ff', borderRadius: 2, border: '1px solid #c5cae9' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Manage Configurations For User
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {user.roleId === 1
                ? 'As Super Admin you can manage configurations for any user.'
                : 'As Client Admin you can manage configurations for users in your organisation.'}
            </Typography>
            <Autocomplete
              size="small"
              sx={{ minWidth: 280, maxWidth: 420 }}
              options={filteredUsers}
              getOptionLabel={(u) => `${u.displayName || u.username} (${u.emailAddress})`}
              renderOption={(props, u) => (
                <li {...props} key={u.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{u.displayName || u.username}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.emailAddress}</Typography>
                  </Box>
                </li>
              )}
              value={filteredUsers.find((u) => u.id === selectedUserId) ?? null}
              onChange={(_, newVal) => setSelectedUserId(newVal?.id ?? user.id)}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField {...params} label="Select user" placeholder="Search name or email…" />
              )}
              ListboxProps={{ sx: { maxHeight: 260 } }}
              noOptionsText="No users found"
            />
          </Box>
        )}

        {/* Config list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : queryError ? (
          <Alert severity="error">
            Failed to load configurations: {queryError.message}
          </Alert>
        ) : configurations.length === 0 ? (
          <Alert severity="info">
            {managingSelf
              ? 'No saved configurations yet. Save your settings from the TalkForTy mobile app to see them here.'
              : 'This user has no saved configurations.'}
          </Alert>
        ) : (
          <List disablePadding>
            {[...configurations]
              .sort(
                (a, b) =>
                  new Date(b.updatedDate ?? b.createdDate).getTime() -
                  new Date(a.updatedDate ?? a.createdDate).getTime()
              )
              .map((cfg, idx) => {
                const parsed = parseCustomData(cfg.customData);
                const voiceName = parsed?.userPreferences?.defaultVoice
                  ? (allVoices.find((v) => v.id === parsed.userPreferences.defaultVoice)?.name ?? '—')
                  : '—';
                const categoryCount = parsed?.categoryOrder?.length ?? 0;
                const customItemCount = parsed?.customItems?.length ?? 0;
                const favoriteCount = parsed?.favorites?.length ?? 0;

                return (
                  <ListItem
                    key={cfg.id}
                    disablePadding
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      mb: 1.5,
                      p: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ width: '100%', p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {cfg.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            Last updated: {formatDate(cfg.updatedDate ?? cfg.createdDate)}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                            {voiceName !== '—' && (
                              <Chip
                                icon={<VoiceIcon sx={{ fontSize: '13px !important' }} />}
                                label={voiceName}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.72rem', height: 22 }}
                              />
                            )}
                            {categoryCount > 0 && (
                              <Chip
                                label={`${categoryCount} categories`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.72rem', height: 22 }}
                              />
                            )}
                            {customItemCount > 0 && (
                              <Chip
                                label={`${customItemCount} custom ${customItemCount === 1 ? 'item' : 'items'}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.72rem', height: 22 }}
                              />
                            )}
                            {favoriteCount > 0 && (
                              <Chip
                                icon={<FavoriteIcon sx={{ fontSize: '12px !important' }} />}
                                label={`${favoriteCount} favorites`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.72rem', height: 22 }}
                              />
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          <Tooltip title="Edit configuration">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setEditTarget(cfg)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete configuration">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteConfirm(cfg)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
          </List>
        )}

      {/* Edit Dialog */}
      {editTarget && (
        <EditDialog
          open={!!editTarget}
          customization={editTarget}
          allCustomizations={configurations}
          allVoices={allVoices}
          targetUserId={targetUserId}
          isAdmin={!managingSelf}
          canGenerateAudio={isPaidUser}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Create Dialog */}
      <EditDialog
        open={createOpen}
        customization={null}
        allCustomizations={configurations}
        allVoices={allVoices}
        targetUserId={targetUserId}
        isAdmin={!managingSelf}
        canGenerateAudio={isPaidUser}
        onClose={() => setCreateOpen(false)}
        onSaved={handleSaved}
      />

      {/* Delete Confirm Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => { setDeleteConfirm(null); setDeleteError(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Configuration?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{deleteConfirm?.label}&quot;? This cannot be undone.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}
            variant="text"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirmed}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
